import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { LookupFieldSelect } from '../CuratedWatches/LookupFieldSelect';
import { LookupMultiSelect } from '../CuratedWatches/LookupMultiSelect';
import { TagInput } from '../CuratedWatches/TagInput';
import { ReferenceImageUpload } from '../common/ReferenceImageUpload';
import { RequestNewValueModal } from './RequestNewValueModal';
import {
  createReferenceFromPortal,
  getCustomCalibres,
  getLookupCollection,
  updateReferenceFromPortal,
  type ReferencePortalEditableFields,
} from '../../services/firebase';
import { normalizeCalibreKey } from '../../services/calibreResolution';
import { isAlgoliaConfigured, searchCalibres } from '../../services/algolia';
import {
  parseDialMarkerValues,
  dialMarkerValuesForWrite,
  formatDialMarkerValues,
} from '../../utils/dialMarkerFields';
import {
  parseMultiLookupValues,
  formatMultiLookupValues,
} from '../../utils/multiLookupValues';
import type { LookupValue, WatchLink, WatchReference } from '../../types';

/**
 * EditMyReferencePanel — Brand Portal Phase 2.2c (chunk iii) / 2.2d (chunk ii).
 *
 * A focused brand-owner reference editor, organised into sections, covering the
 * reference fields a brand owner may change. Mirrors `EditMyBrandPanel`: it
 * renders the form body only and the caller wraps it in a `<Modal size='lg'>`
 * (chunk iv). Saves go through the `updateReferenceFromPortal` callable — the
 * server-side whitelist is the source of truth for what's editable; anything
 * outside it (editorial/curation flags, aiDescription, searchText,
 * referenceNormalized, …) is silently dropped server-side. We don't refactor
 * the super-admin `ReferenceEditor`.
 *
 * ── CREATE vs EDIT (Phase 2.2d chunk ii) ──────────────────────────────────────
 * The same panel does double duty via the `mode` prop:
 *   • `mode='edit'` (default) — seeds from the passed `reference` doc and saves
 *     through `updateReferenceFromPortal`. Unchanged from before.
 *   • `mode='create'` — starts empty (no seed doc), saves through
 *     `createReferenceFromPortal`. On success it does NOT close: it transitions
 *     IN PLACE to edit mode against the just-created doc (so the image upload —
 *     which needs an existing refId — becomes available) and lets the user keep
 *     editing. The image fieldset is hidden until that transition happens.
 * Server hard-requires only the ref number; `model` is a client soft-nudge
 * (asterisk + helper text, never blocks submit).
 *
 * Editorial/curation flags (isPromoted, isCurated, showInExplore) are
 * deliberately ABSENT — those stay super-admin-only. Calibre is select-only
 * (pick an existing calibre; no free-text create) with a disabled "Request new
 * calibre →" affordance for the future Phase 2.5 request flow.
 *
 * ── WHOLE-REPLACE SEMANTICS FOR NESTED OBJECTS (the single biggest correctness
 * risk) ──────────────────────────────────────────────────────────────────────
 * The callable writes each nested object (`movement`, `case`, `dialAndHands`,
 * `strap`, `electronics`, `production`) with Firestore `update()`, which
 * WHOLE-REPLACES the object. This form only surfaces a SUBSET of each object's
 * subkeys, so on save every nested object is built as
 *     { ...(reference.<obj> ?? {}), <exposed overrides> }
 * — spreading the COMPLETE existing object first, then overriding only the
 * fields this form exposes. Unexposed subkeys (e.g. movement.escapement,
 * movement.date[], dialAndHands.lume, electronics.timekeeping, case.code) are
 * preserved. Exposed fields the user clears become `undefined`, which the
 * callable's JSON serialization drops, so the key is removed from the stored
 * object (the desired "cleared" behaviour). NEVER send a freshly-built partial
 * nested object — it would silently drop every unsent subkey.
 */

interface EditMyReferencePanelProps {
  brandId: string;
  /** 'create' starts empty; 'edit' (default) seeds from `reference`. */
  mode?: 'create' | 'edit';
  /** FULL doc (chunk iv re-fetches before opening). Required for edit, omitted for create. */
  reference?: WatchReference;
  /**
   * Fired after a successful save with the resulting full doc (merged updates
   * for edit, the server-returned doc for create) so the parent can reflect it
   * in its list. The parent decides replace-vs-prepend by whether the id exists.
   */
  onSaved?: (reference: WatchReference) => void;
  onClose: () => void;
  /**
   * Create-mode only: invoked when the user hits a duplicate ref number and the
   * server reports the conflicting doc's id, so the parent can open it for edit.
   */
  onEditExisting?: (referenceId: string) => void;
}

/** Parse a string field to a number, returning undefined for empty/NaN. */
const numOrUndef = (
  s: string,
  parse: (v: string) => number,
): number | undefined => {
  const t = s.trim();
  if (t === '') return undefined;
  const n = parse(t);
  return Number.isNaN(n) ? undefined : n;
};

const inputClass =
  'w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500';
const errorInputClass =
  'w-full border border-red-300 bg-red-50 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500';
const fieldsetClass = 'space-y-4 rounded-lg border border-gray-200 p-4';
const legendClass = 'px-1 text-sm font-semibold text-gray-900';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

/**
 * Long enough that a typing burst is one request, short enough that the
 * suggestions feel live. Matches `useCalibreSearch` and `useBrandSearch`.
 */
const CALIBRE_DEBOUNCE_MS = 250;

/**
 * How many suggestions one search returns and the dropdown shows. The old
 * local filter capped its display at 50 over the whole collection; this is the
 * same ceiling, now applied at the source.
 */
const CALIBRE_SUGGESTION_LIMIT = 50;

/**
 * Select-only calibre picker. Brand owners may pick an EXISTING calibre but
 * cannot mint new ones via free text (per James's decision); the disabled
 * "Request new calibre →" affordance is wired up in Phase 2.5.
 *
 * Suggestions come from the `watchkeeper_calibres` Algolia index, searched as
 * the user types. The committed value only changes when a suggestion is chosen
 * (or a typed value exactly matches a suggestion on blur) — arbitrary free text
 * is reverted, so no unknown calibre can be persisted.
 *
 * Searching the index rather than loading `custom_calibres` also settles the
 * read-scope question this component used to carry a flag about: the portal's
 * Firestore rules may well not permit reading the whole calibre collection (see
 * `getCalibresCountForBrand`'s doc comment), but the Algolia search key is
 * public and index-scoped, so the picker works regardless. The Firestore load
 * remains only as the offline fallback, and if IT fails the field still degrades
 * to a read-only display of the current calibre plus the request affordance
 * rather than breaking the form.
 *
 * One deliberate difference from the old behaviour: `getCustomCalibres()` hid
 * `deprecated` calibres (redirect aliases) and the index carries no `deprecated`
 * field to filter on, so a redirect alias can now appear as a suggestion. The
 * name it offers still resolves — to the calibre it redirects to — which is why
 * this is accepted rather than paid for with a per-suggestion document read the
 * portal's read scope may not even allow.
 */
function CalibrePicker({
  value,
  onChange,
  disabled = false,
  onRequestNewValue,
  requestSent = false,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  // Phase 2.5: when provided, the "Request new calibre →" affordance becomes a
  // live button seeded with the current query. The parent owns the modal.
  onRequestNewValue?: (seed: { query?: string }) => void;
  requestSent?: boolean;
}) {
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState(value);
  const [names, setNames] = useState<string[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Guards against a slow response for "sw2" overwriting one for "sw200-1". */
  const requestSeq = useRef(0);
  /** Offline fallback: the whole collection, fetched at most once. */
  const fallbackNames = useRef<Promise<string[]> | null>(null);

  // Keep the visible query in sync if the committed value changes externally.
  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), CALIBRE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(
    () => () => {
      if (blurTimer.current) clearTimeout(blurTimer.current);
    },
    [],
  );

  useEffect(() => {
    const seq = ++requestSeq.current;

    const loadFallbackNames = async () => {
      if (!fallbackNames.current) {
        fallbackNames.current = getCustomCalibres().then((entries) =>
          Array.from(
            new Set(
              entries
                .map((e) => e.name)
                .filter((n): n is string => typeof n === 'string' && !!n.trim()),
            ),
          ).sort((a, b) => a.localeCompare(b)),
        );
      }
      const all = await fallbackNames.current;
      // Normalized on both sides so "caliber"/"calibre" search interchangeably.
      const q = normalizeCalibreKey(debouncedQuery);
      return q ? all.filter((n) => normalizeCalibreKey(n).includes(q)) : all;
    };

    const run = async () => {
      if (isAlgoliaConfigured()) {
        try {
          const res = await searchCalibres(
            debouncedQuery,
            {},
            0,
            CALIBRE_SUGGESTION_LIMIT,
            { attributes: ['objectID', 'name'] },
          );
          if (seq !== requestSeq.current) return;
          setNames(
            res.hits
              .map((h) => h.name)
              .filter((n): n is string => !!n && !!n.trim()),
          );
          return;
        } catch (error) {
          console.warn('Calibre search unavailable, using a full scan', error);
        }
      }
      const rows = await loadFallbackNames();
      if (seq !== requestSeq.current) return;
      setNames(rows);
    };

    run().catch(() => {
      // Neither source answered — the read scope may not allow the collection
      // read either. Degrade to a read-only field rather than an empty picker
      // that silently reverts everything the user types.
      if (seq === requestSeq.current) setLoadFailed(true);
    });
  }, [debouncedQuery]);

  const filtered = names.slice(0, CALIBRE_SUGGESTION_LIMIT);

  const commitFromQuery = async () => {
    // Select-only: accept the query only if it exactly matches a known calibre
    // (case-insensitive); otherwise revert to the last committed value.
    const typed = normalizeCalibreKey(query);
    const match = names.find((n) => normalizeCalibreKey(n) === typed);
    if (match) {
      onChange(match);
      setQuery(match);
      return;
    }
    // The suggestion list is now one page of search results rather than every
    // calibre, so a name typed out in full and blurred inside the debounce
    // window may not be in it yet. Confirm against the index before reverting —
    // silently discarding a correctly typed calibre would be worse than one
    // extra request on blur.
    if (typed && isAlgoliaConfigured()) {
      try {
        const res = await searchCalibres(query, {}, 0, CALIBRE_SUGGESTION_LIMIT, {
          attributes: ['objectID', 'name'],
        });
        const exact = res.hits
          .map((h) => h.name)
          .find((n): n is string => !!n && normalizeCalibreKey(n) === typed);
        if (exact) {
          onChange(exact);
          setQuery(exact);
          return;
        }
      } catch {
        // Fall through to the revert — an unconfirmable value isn't committable.
      }
    }
    setQuery(value);
  };

  // Degraded mode: list unavailable in this read scope. Show the current value
  // read-only so the form still works for the other fields.
  if (loadFailed) {
    return (
      <div>
        <label className={labelClass}>Calibre</label>
        <input
          type='text'
          value={value}
          readOnly
          disabled
          className='w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 text-gray-500'
        />
        <p className='mt-1 text-xs text-gray-400'>
          Calibre list unavailable here. Contact support to change the calibre.
        </p>
      </div>
    );
  }

  return (
    <div>
      <label className={labelClass}>Calibre</label>
      <div className='relative'>
        <input
          type='text'
          value={query}
          disabled={disabled}
          placeholder='Search calibres…'
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            // Delay so an option's onMouseDown/onClick can fire first.
            blurTimer.current = setTimeout(() => {
              setOpen(false);
              void commitFromQuery();
            }, 150);
          }}
          className={inputClass}
        />
        {open && filtered.length > 0 && (
          <ul className='absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg'>
            {filtered.map((name) => (
              <li key={name}>
                <button
                  type='button'
                  // Prevent the input blur from firing before the click commits.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onChange(name);
                    setQuery(name);
                    setOpen(false);
                  }}
                  className='block w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100'
                >
                  {name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {value && (
        <button
          type='button'
          onClick={() => {
            onChange('');
            setQuery('');
          }}
          disabled={disabled}
          className='mt-1 text-xs text-gray-500 underline hover:text-gray-700 disabled:opacity-50'
        >
          Clear calibre
        </button>
      )}
      {/* Gated action — live request flow (Phase 2.5). */}
      <div className='mt-1'>
        {onRequestNewValue ? (
          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={() => onRequestNewValue({ query: query.trim() || undefined })}
              disabled={disabled}
              className='text-xs text-brand-600 underline hover:text-brand-700 disabled:opacity-50'
            >
              + Request new calibre
            </button>
            {requestSent && (
              <span className='text-xs text-green-600'>✓ Request sent</span>
            )}
          </div>
        ) : (
          <button
            type='button'
            aria-disabled='true'
            title="Don't see yours? Contact support to add it"
            className='text-xs text-gray-400 cursor-not-allowed'
          >
            Request new calibre →
          </button>
        )}
      </div>
    </div>
  );
}

export function EditMyReferencePanel({
  brandId,
  mode = 'edit',
  reference,
  onSaved,
  onClose,
  onEditExisting,
}: EditMyReferencePanelProps) {
  // `currentMode` starts at the prop but flips to 'edit' in place after a
  // successful create; `currentReference` is the live doc that backs the
  // image upload + nested whole-replace spreads (undefined until a create
  // lands). Field state below seeds from `seed` — the passed doc, or `{}` in
  // create mode so every field starts empty.
  const [currentMode, setCurrentMode] = useState<'create' | 'edit'>(mode);
  const [currentReference, setCurrentReference] = useState<
    WatchReference | undefined
  >(reference);
  const seed = reference ?? ({} as Partial<WatchReference>);

  // ── Identity ────────────────────────────────────────────────────────────
  const [refNumber, setRefNumber] = useState(seed.reference ?? '');
  const [model, setModel] = useState(seed.model ?? '');
  const [collection, setCollection] = useState(seed.collection ?? '');
  const [series, setSeries] = useState(seed.series ?? '');
  const [productName, setProductName] = useState(seed.productName ?? '');
  const [nickname, setNickname] = useState(seed.nickname ?? '');
  const [watchType, setWatchType] = useState(seed.watchType ?? '');
  const [occasion, setOccasion] = useState<string[]>(() => {
    const raw = seed.occasion;
    if (!raw) return [];
    if (Array.isArray(raw))
      return raw.filter((v): v is string => typeof v === 'string' && !!v.trim());
    return raw
      .split(/[,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
  });
  const [specialEdition, setSpecialEdition] = useState(
    seed.specialEdition === true,
  );
  const [knownDiscontinuation, setKnownDiscontinuation] = useState(
    seed.knownDiscontinuation === true,
  );

  // ── watchType-driven section visibility ───────────────────────────────────
  // Mirrors the super-admin ReferenceEditor (lines ~167-192): load the
  // lookup_watch_types collection and read the selected type's `properties`
  // flags to decide whether the Movement vs Electronics fieldset is relevant.
  // (ReferenceEditor is NOT modified — we just reuse its read pattern.)
  const [watchTypeLookupValues, setWatchTypeLookupValues] = useState<
    LookupValue[]
  >([]);
  useEffect(() => {
    getLookupCollection('lookup_watch_types')
      .then(setWatchTypeLookupValues)
      .catch(() => {});
  }, []);

  const watchTypeProperties = useMemo(() => {
    if (!watchType || watchTypeLookupValues.length === 0) return [];
    const valueLower = watchType.toLowerCase();
    const match = watchTypeLookupValues.find(
      (lv) =>
        lv.value === watchType ||
        lv.displayName === watchType ||
        lv.value.toLowerCase() === valueLower ||
        lv.displayName.toLowerCase() === valueLower ||
        lv.synonyms?.some((s) => s.toLowerCase() === valueLower),
    );
    return match?.properties ?? [];
  }, [watchType, watchTypeLookupValues]);

  const showMovement = watchTypeProperties.includes('showMovement');
  const showModule = watchTypeProperties.includes('showModule');

  // Visibility rules:
  //  • no watchType selected → show BOTH (let the user see and decide)
  //  • the matched type configures neither flag → show BOTH (don't leave an
  //    unconfigured type with no movement/electronics fields = unusable form)
  //  • otherwise honour the per-type flags.
  // NOTE: form state always holds BOTH `movement` and `electronics` regardless
  // of visibility — hiding a fieldset never drops its data (whole-replace on
  // save needs the full nested object), so toggling watchType is non-destructive.
  const hasTypeFlags = showMovement || showModule;
  const renderMovementSection = !watchType || !hasTypeFlags || showMovement;
  const renderElectronicsSection = !watchType || !hasTypeFlags || showModule;

  // ── Image ───────────────────────────────────────────────────────────────
  // ReferenceImageUpload owns a single main image as `imageUrl` (a storage path
  // from upload OR an http URL from paste) plus a sourceUrl.
  const initialImageUrl =
    seed.images?.[0]?.storagePath ?? seed.imageUrls?.[0] ?? '';
  const initialSourceUrl = seed.images?.[0]?.sourceUrl ?? '';
  const [imageUrl, setImageUrl] = useState(initialImageUrl);
  const [sourceUrl, setSourceUrl] = useState(initialSourceUrl);

  // ── Case ────────────────────────────────────────────────────────────────
  const [caseDiameter, setCaseDiameter] = useState(
    seed.case?.diameterMm?.toString() ||
      seed.case?.sizeMm?.toString() ||
      seed.case?.diameter?.toString() ||
      '',
  );
  const [caseThickness, setCaseThickness] = useState(
    seed.case?.heightMm?.toString() ||
      seed.case?.thickness?.toString() ||
      '',
  );
  const [caseLugToLug, setCaseLugToLug] = useState(
    seed.case?.lugToLugMm?.toString() ||
      seed.case?.lugToLug?.toString() ||
      '',
  );
  const [caseLugWidth, setCaseLugWidth] = useState(
    seed.case?.lugWidthMm?.toString() ||
      seed.case?.lugWidth?.toString() ||
      '',
  );
  const [caseMaterial, setCaseMaterial] = useState(seed.case?.material ?? '');
  const [caseShape, setCaseShape] = useState(seed.case?.shape ?? '');
  const [caseWaterResistance, setCaseWaterResistance] = useState(
    seed.case?.waterResistanceM?.toString() ||
      seed.case?.waterResistance ||
      '',
  );
  const [caseBack, setCaseBack] = useState(
    seed.case?.caseBack || seed.case?.caseback || '',
  );
  const [caseCoating, setCaseCoating] = useState(seed.case?.coating ?? '');
  const [caseFinish, setCaseFinish] = useState(seed.case?.finish ?? '');
  const [caseWeightG, setCaseWeightG] = useState(
    seed.case?.weightG?.toString() ?? '',
  );

  // ── Dial & Hands ──────────────────────────────────────────────────────────
  const [dialColor, setDialColor] = useState(seed.dialAndHands?.color ?? '');
  const [dialMaterial, setDialMaterial] = useState(
    seed.dialAndHands?.material ?? '',
  );
  const [dialFinish, setDialFinish] = useState(
    seed.dialAndHands?.finish ?? '',
  );
  const [dialHands, setDialHands] = useState(seed.dialAndHands?.hands ?? '');
  const [dialHandsColor, setDialHandsColor] = useState(
    seed.dialAndHands?.handsColor ?? '',
  );
  // Marker type / marker style persist as string[] (shared-types v1.51.0), but
  // this form is free-text rather than a lookup picker — so hold the comma-
  // joined display string in state and split it on write. formatDialMarkerValues
  // also renders the pre-migration scalar, so a ref that has not been through
  // migrateDialIndexesNumerals still shows its value here.
  const [dialIndexes, setDialIndexes] = useState(() =>
    formatDialMarkerValues(seed.dialAndHands?.indexes),
  );
  const [dialNumerals, setDialNumerals] = useState(() =>
    formatDialMarkerValues(seed.dialAndHands?.numerals),
  );
  const [dialLuminova, setDialLuminova] = useState(
    seed.dialAndHands?.luminova === true,
  );
  const [dialSubdials, setDialSubdials] = useState(
    seed.dialAndHands?.subdials?.toString() ?? '',
  );

  // ── Bezel & Crystal (top-level fields) ────────────────────────────────────
  const [bezel, setBezel] = useState(seed.bezel ?? '');
  const [bezelType, setBezelType] = useState(seed.bezelType ?? '');
  // Crown Type and Crystal are multi-select and persist as string[] on the
  // reference — but this form is free text, and watchlock's portal callable
  // validates BOTH as `asString` (referencePortalShared.ts FIELD_VALIDATORS),
  // so an array sent from here is silently dropped. Hold the comma-joined
  // display string in state and keep writing a string; the admin read side
  // splits it back. formatMultiLookupValues also renders a pre-migration
  // scalar, so a ref that has not been through migrateCrownTypeCrystal shows
  // its value here.
  // FOLLOW-UP (watchlock): flip these two to `asStringArray` and write the
  // array from here, so a portal edit stops re-introducing the scalar shape.
  const [crownType, setCrownType] = useState(() =>
    formatMultiLookupValues(seed.crownType),
  );
  const [glass, setGlass] = useState(seed.glass ?? '');
  const [crystal, setCrystal] = useState(() =>
    formatMultiLookupValues(seed.crystal),
  );
  const [dial, setDial] = useState(seed.dial ?? '');

  // ── Movement ──────────────────────────────────────────────────────────────
  const [calibre, setCalibre] = useState(seed.calibre ?? '');
  const [movementType, setMovementType] = useState(seed.movementType ?? '');
  const [frequencyVph, setFrequencyVph] = useState(
    seed.movement?.frequencyVph?.toString() ?? '',
  );
  const [jewels, setJewels] = useState(
    seed.movement?.jewels?.toString() ?? '',
  );
  const [powerReserveHours, setPowerReserveHours] = useState(
    seed.movement?.powerReserveHours?.toString() ?? '',
  );
  const [baseMovement, setBaseMovement] = useState(
    seed.movement?.baseMovement ?? '',
  );
  const [isChronometer, setIsChronometer] = useState(
    seed.movement?.isChronometer === true,
  );
  const [isCosc, setIsCosc] = useState(
    seed.movement?.isCosc === true || seed.movement?.isCOSC === true,
  );
  const [isGenevaSeal, setIsGenevaSeal] = useState(
    seed.movement?.isGenevaSeal === true,
  );
  const [isMETAS, setIsMETAS] = useState(seed.movement?.isMETAS === true);
  const [isChronograph, setIsChronograph] = useState(
    seed.movement?.isChronograph === true,
  );
  const [isGMT, setIsGMT] = useState(seed.movement?.isGMT === true);
  const [isInHouse, setIsInHouse] = useState(
    seed.movement?.isInHouse === true,
  );
  const [movementDiameterMm, setMovementDiameterMm] = useState(
    seed.movement?.diameterMm?.toString() ?? '',
  );
  const [movementHeightMm, setMovementHeightMm] = useState(
    seed.movement?.heightMm?.toString() ?? '',
  );
  const [functions, setFunctions] = useState<string[]>(seed.functions ?? []);

  // ── Electronics ───────────────────────────────────────────────────────────
  const [elecModule, setElecModule] = useState(seed.electronics?.module ?? '');
  const [elecBattery, setElecBattery] = useState(
    seed.electronics?.battery ?? '',
  );
  const [elecBatteryLife, setElecBatteryLife] = useState(
    seed.electronics?.batteryLife ?? '',
  );
  const [elecChargeTime, setElecChargeTime] = useState(
    seed.electronics?.chargeTime ?? '',
  );
  const [elecDisplay, setElecDisplay] = useState(
    seed.electronics?.display ?? '',
  );
  const [elecLcdType, setElecLcdType] = useState(
    seed.electronics?.lcdType ?? '',
  );
  const [elecLightType, setElecLightType] = useState(
    seed.electronics?.lightType ?? '',
  );

  // ── Strap ─────────────────────────────────────────────────────────────────
  const [strapMaterial, setStrapMaterial] = useState(
    seed.strap?.material ?? '',
  );
  const [strapColor, setStrapColor] = useState(seed.strap?.color ?? '');
  const [strapWidth, setStrapWidth] = useState(
    seed.strap?.width?.toString() ?? '',
  );
  const [strapBuckle, setStrapBuckle] = useState(seed.strap?.buckleType ?? '');
  const [strapMicroAdjustable, setStrapMicroAdjustable] = useState(
    seed.strap?.isMicroAdjustable === true,
  );

  // ── Production ──────────────────────────────────────────────────────────────
  const [prodStartYear, setProdStartYear] = useState(
    seed.production?.startYear?.toString() ?? '',
  );
  const [prodEndYear, setProdEndYear] = useState(
    seed.production?.endYear?.toString() ?? '',
  );
  const [prodReleaseYear, setProdReleaseYear] = useState(
    seed.production?.releaseYear?.toString() ?? '',
  );
  const [prodIsLimited, setProdIsLimited] = useState(
    seed.production?.isLimited === true,
  );
  const [prodLimitedSize, setProdLimitedSize] = useState(
    seed.production?.limitedSize?.toString() ?? '',
  );

  // ── Links ───────────────────────────────────────────────────────────────
  const [productUrl, setProductUrl] = useState(seed.productUrl ?? '');
  const [links, setLinks] = useState<WatchLink[]>(seed.links ?? []);

  // ── Description ───────────────────────────────────────────────────────────
  const [description, setDescription] = useState(seed.description ?? '');
  // `useAuthoredDescription` is brand-owner editable (in the callable whitelist)
  // but isn't on the shared WatchReference type yet — read it via a narrow cast.
  const [useAuthoredDescription, setUseAuthoredDescription] = useState(
    (seed as { useAuthoredDescription?: boolean }).useAuthoredDescription ===
      true,
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Entity-request modal (Phase 2.5) ──────────────────────────────────────
  // Which picker raised a "Request new value", what to seed it with, and which
  // pickers have had a request sent this session (drives the inline chip). The
  // chip is keyed by `sourceField` so each affordance tracks its own state.
  const [requestModal, setRequestModal] = useState<{
    type: 'calibre' | 'lookup';
    collection?: string;
    sourceField: string;
    seed?: string;
  } | null>(null);
  const [sentRequestFields, setSentRequestFields] = useState<Set<string>>(
    () => new Set(),
  );

  const refInvalid = refNumber.trim() === '';

  const addLink = () => setLinks([...links, { url: '' }]);
  const updateLink = (index: number, patch: Partial<WatchLink>) =>
    setLinks(links.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  const removeLink = (index: number) =>
    setLinks(links.filter((_, i) => i !== index));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving || refInvalid) return;

    const caseDiameterVal = numOrUndef(caseDiameter, parseFloat);
    const caseThicknessVal = numOrUndef(caseThickness, parseFloat);
    const caseLugToLugVal = numOrUndef(caseLugToLug, parseFloat);
    const caseLugWidthVal = numOrUndef(caseLugWidth, parseFloat);
    const caseWaterResVal = numOrUndef(caseWaterResistance, parseFloat);

    const updates: ReferencePortalEditableFields = {
      // Identity
      reference: refNumber.trim(),
      model: model.trim(),
      collection: collection.trim(),
      series: series.trim(),
      productName: productName.trim(),
      nickname: nickname.trim(),
      watchType: watchType.trim(),
      // Multi-select in the UI; persisted as a comma-joined string for backend.
      occasion: occasion.join(', '),
      specialEdition,
      knownDiscontinuation,

      // Bezel & Crystal (top-level)
      bezel: bezel.trim(),
      bezelType: bezelType.trim(),
      // Re-joined through the parser so a stray "sapphire,,cyclops_lens" or a
      // trailing separator is normalised on the way out — the callable stores
      // exactly this string and the admin splits it on the next read.
      crownType: parseMultiLookupValues(crownType).join(', '),
      glass: glass.trim(),
      crystal: parseMultiLookupValues(crystal).join(', '),
      dial: dial.trim(),

      // Movement (top-level)
      calibre: calibre.trim(),
      movementType: movementType.trim(),
      functions,

      // Links / description (top-level)
      productUrl: productUrl.trim(),
      links: links.filter((l) => l.url.trim() !== ''),
      description,
      useAuthoredDescription,

      // ── Nested objects: WHOLE-REPLACE — spread the COMPLETE existing object,
      // then override only the fields this form exposes (see file-top comment).
      case: {
        ...(currentReference?.case ?? {}),
        diameter: caseDiameterVal,
        diameterMm: caseDiameterVal,
        sizeMm: caseDiameterVal,
        thickness: caseThicknessVal,
        heightMm: caseThicknessVal,
        lugToLug: caseLugToLugVal,
        lugToLugMm: caseLugToLugVal,
        lugWidth: caseLugWidthVal,
        lugWidthMm: caseLugWidthVal,
        material: caseMaterial.trim() || undefined,
        shape: caseShape.trim() || undefined,
        waterResistance: caseWaterResistance.trim() || undefined,
        waterResistanceM: caseWaterResVal,
        caseBack: caseBack.trim() || undefined,
        caseback: caseBack.trim() || undefined,
        coating: caseCoating.trim() || undefined,
        finish: caseFinish.trim() || undefined,
        weightG: numOrUndef(caseWeightG, parseFloat),
      },
      dialAndHands: {
        ...(currentReference?.dialAndHands ?? {}),
        color: dialColor.trim() || undefined,
        material: dialMaterial.trim() || undefined,
        finish: dialFinish.trim() || undefined,
        hands: dialHands.trim() || undefined,
        handsColor: dialHandsColor.trim() || undefined,
        indexes: dialMarkerValuesForWrite(parseDialMarkerValues(dialIndexes)),
        numerals: dialMarkerValuesForWrite(parseDialMarkerValues(dialNumerals)),
        luminova: dialLuminova,
        // Dual-name trap shim — the RN app reads only `lume`, so a ref saved
        // with just `luminova` renders with no "Luminous" row.
        lume: dialLuminova,
        subdials: numOrUndef(dialSubdials, parseInt),
      },
      movement: {
        ...(currentReference?.movement ?? {}),
        frequencyVph: numOrUndef(frequencyVph, parseInt),
        jewels: numOrUndef(jewels, parseInt),
        powerReserveHours: numOrUndef(powerReserveHours, parseInt),
        baseMovement: baseMovement.trim() || undefined,
        isChronometer,
        isCosc,
        // Dual-name trap shim — the RN app's COSC certification chip reads only
        // `isCOSC`, so writing just the admin spelling suppressed it entirely.
        isCOSC: isCosc,
        isGenevaSeal,
        isMETAS,
        isChronograph,
        isGMT,
        isInHouse,
        diameterMm: numOrUndef(movementDiameterMm, parseFloat),
        heightMm: numOrUndef(movementHeightMm, parseFloat),
      },
      electronics: {
        ...(currentReference?.electronics ?? {}),
        module: elecModule.trim() || undefined,
        battery: elecBattery.trim() || undefined,
        batteryLife: elecBatteryLife.trim() || undefined,
        chargeTime: elecChargeTime.trim() || undefined,
        display: elecDisplay.trim() || undefined,
        lcdType: elecLcdType.trim() || undefined,
        lightType: elecLightType.trim() || undefined,
      },
      strap: {
        ...(currentReference?.strap ?? {}),
        material: strapMaterial.trim() || undefined,
        color: strapColor.trim() || undefined,
        width: numOrUndef(strapWidth, parseFloat),
        buckleType: strapBuckle.trim() || undefined,
        isMicroAdjustable: strapMicroAdjustable,
      },
      production: {
        ...(currentReference?.production ?? {}),
        startYear: numOrUndef(prodStartYear, parseInt),
        endYear: numOrUndef(prodEndYear, parseInt),
        releaseYear: numOrUndef(prodReleaseYear, parseInt),
        isLimited: prodIsLimited,
        limitedSize: numOrUndef(prodLimitedSize, parseInt),
      },
    };

    // Image fields: only touch them when the main image actually changed, so we
    // never clobber existing images/imageUrls on an untouched reference.
    // galleryImages is a separate field and is never sent here.
    const imageChanged =
      imageUrl !== initialImageUrl ||
      sourceUrl !== initialSourceUrl;
    if (imageChanged) {
      const url = imageUrl.trim();
      if (!url) {
        updates.images = [];
        updates.imageUrls = [];
      } else if (url.startsWith('http')) {
        // Direct URL → imageUrls[]; storage-backed images[] cleared.
        updates.imageUrls = [url];
        updates.images = [];
      } else {
        // Storage path → images[] (ImageInfo). contentType isn't surfaced by
        // ReferenceImageUpload, so we keep the existing one or default it.
        const info: Record<string, unknown> = {
          storagePath: url,
          contentType: currentReference?.images?.[0]?.contentType ?? 'image/jpeg',
          index: 0,
        };
        if (sourceUrl.trim()) info.sourceUrl = sourceUrl.trim();
        updates.images = [info];
        updates.imageUrls = [];
      }
    }

    setSaving(true);
    setError(null);
    try {
      if (currentMode === 'create') {
        // Server hard-requires `reference`; everything else is optional. The
        // returned doc carries the new id + resolved server fields, which we
        // adopt so the form can keep editing (image upload now has a refId).
        const { reference: createdRef } = await createReferenceFromPortal(
          brandId,
          { ...updates, reference: refNumber.trim() },
        );
        setCurrentReference(createdRef);
        setCurrentMode('edit');
        toast.success(
          'Reference created — you can now add an image and continue editing',
        );
        onSaved?.(createdRef);
        // Deliberately NOT closing: transition in place to edit mode.
      } else {
        const result = await updateReferenceFromPortal(
          brandId,
          currentReference!.id,
          updates,
        );
        toast.success(
          result.fieldsUpdated.length > 0
            ? `Reference saved (${result.fieldsUpdated.length} field${
                result.fieldsUpdated.length === 1 ? '' : 's'
              } updated)`
            : 'Reference saved',
        );
        // Hand the parent the merged full doc so it can reflect the row.
        onSaved?.({ ...currentReference, ...updates } as WatchReference);
        onClose();
      }
    } catch (err) {
      // Duplicate ref number → offer a jump to the existing doc when the server
      // tells us its id (details.existingReferenceId). Firebase callable codes
      // arrive as either 'already-exists' or 'functions/already-exists'.
      const code = (err as { code?: unknown }).code;
      const isDuplicate =
        typeof code === 'string' && code.endsWith('already-exists');
      const existingId = (
        err as { details?: { existingReferenceId?: unknown } }
      ).details?.existingReferenceId;

      if (isDuplicate) {
        const msg = 'A reference with this number already exists.';
        setError(msg);
        if (typeof existingId === 'string' && existingId && onEditExisting) {
          toast(
            (t) => (
              <span className='flex items-center gap-3'>
                {msg}
                <button
                  type='button'
                  onClick={() => {
                    toast.dismiss(t.id);
                    onEditExisting(existingId);
                  }}
                  className='underline font-medium text-brand-700 hover:text-brand-800'
                >
                  Edit existing →
                </button>
              </span>
            ),
            { duration: 8000 },
          );
        } else {
          toast.error(msg);
        }
      } else {
        const message =
          err instanceof Error ? err.message : 'Failed to save changes';
        setError(message);
        toast.error(message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <form onSubmit={handleSubmit} className='space-y-5'>
      <p className='text-sm text-gray-500'>
        {currentMode === 'create'
          ? 'New reference'
          : `Edit Reference · ${currentReference?.reference || 'Untitled'}`}
      </p>

      {/* ── Identity ───────────────────────────────────────────────── */}
      <fieldset className={fieldsetClass}>
        <legend className={legendClass}>Identity</legend>

        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
          <div>
            <label className={labelClass}>
              Reference number<span className='text-red-500'> *</span>
            </label>
            <input
              type='text'
              value={refNumber}
              onChange={(e) => setRefNumber(e.target.value)}
              className={refInvalid ? errorInputClass : inputClass}
            />
            {refInvalid && (
              <p className='mt-1 text-xs text-red-600'>
                Reference number is required.
              </p>
            )}
          </div>
          <div>
            <label className={labelClass}>
              Model<span className='text-red-500'> *</span>
            </label>
            <input
              type='text'
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className={inputClass}
            />
            {/* Soft nudge only — never blocks submit. */}
            <p className='mt-1 text-xs text-gray-400'>
              Recommended — helps your customers find this watch.
            </p>
          </div>
        </div>

        <div className='grid grid-cols-1 gap-4 sm:grid-cols-3'>
          <div>
            <label className={labelClass}>Collection</label>
            <input
              type='text'
              value={collection}
              onChange={(e) => setCollection(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Series</label>
            <input
              type='text'
              value={series}
              onChange={(e) => setSeries(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Product name</label>
            <input
              type='text'
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label className={labelClass}>Nickname</label>
          <input
            type='text'
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            className={inputClass}
          />
        </div>

        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
          <LookupFieldSelect
            label='Watch type'
            value={watchType}
            lookupCollection='lookup_watch_types'
            onChange={setWatchType}
            allowCustom={false}
            onRequestNewValue={({ query }) =>
              setRequestModal({
                type: 'lookup',
                collection: 'lookup_watch_types',
                sourceField: 'watchType',
                seed: query,
              })
            }
            requestSent={sentRequestFields.has('watchType')}
          />
          <LookupMultiSelect
            label='Occasion'
            values={occasion}
            lookupCollection='lookup_watch_occasion'
            onChange={setOccasion}
            allowCustom={false}
            onRequestNewValue={() =>
              setRequestModal({
                type: 'lookup',
                collection: 'lookup_watch_occasion',
                sourceField: 'occasion',
              })
            }
            requestSent={sentRequestFields.has('occasion')}
          />
        </div>

        <div className='flex flex-wrap gap-6'>
          <label className='flex items-center gap-2 text-sm text-gray-700'>
            <input
              type='checkbox'
              checked={specialEdition}
              onChange={(e) => setSpecialEdition(e.target.checked)}
              className='h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500'
            />
            Special edition
          </label>
          <label className='flex items-center gap-2 text-sm text-gray-700'>
            <input
              type='checkbox'
              checked={knownDiscontinuation}
              onChange={(e) => setKnownDiscontinuation(e.target.checked)}
              className='h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500'
            />
            Discontinued
          </label>
        </div>
      </fieldset>

      {/* ── Image ──────────────────────────────────────────────────── */}
      {/* Hidden in create mode: image upload needs an existing refId. It
          re-appears once a successful create transitions the panel to edit. */}
      {currentMode === 'edit' && currentReference && (
        <fieldset className={fieldsetClass}>
          <legend className={legendClass}>Image</legend>
          <ReferenceImageUpload
            brandId={brandId}
            referenceId={currentReference.id}
            imageUrl={imageUrl}
            sourceUrl={sourceUrl}
            onImageUrlChange={setImageUrl}
            onSourceUrlChange={setSourceUrl}
          />
        </fieldset>
      )}

      {/* ── Case ───────────────────────────────────────────────────── */}
      <fieldset className={fieldsetClass}>
        <legend className={legendClass}>Case</legend>
        <div className='grid grid-cols-2 gap-4 sm:grid-cols-4'>
          <div>
            <label className={labelClass}>Diameter (mm)</label>
            <input
              type='number'
              value={caseDiameter}
              onChange={(e) => setCaseDiameter(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Thickness (mm)</label>
            <input
              type='number'
              value={caseThickness}
              onChange={(e) => setCaseThickness(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Lug-to-lug (mm)</label>
            <input
              type='number'
              value={caseLugToLug}
              onChange={(e) => setCaseLugToLug(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Lug width (mm)</label>
            <input
              type='number'
              value={caseLugWidth}
              onChange={(e) => setCaseLugWidth(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
        <div className='grid grid-cols-2 gap-4 sm:grid-cols-3'>
          <div>
            <label className={labelClass}>Material</label>
            <input
              type='text'
              value={caseMaterial}
              onChange={(e) => setCaseMaterial(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Shape</label>
            <input
              type='text'
              value={caseShape}
              onChange={(e) => setCaseShape(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Water resistance (m)</label>
            <input
              type='number'
              value={caseWaterResistance}
              onChange={(e) => setCaseWaterResistance(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Case back</label>
            <input
              type='text'
              value={caseBack}
              onChange={(e) => setCaseBack(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Coating</label>
            <input
              type='text'
              value={caseCoating}
              onChange={(e) => setCaseCoating(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Finish</label>
            <input
              type='text'
              value={caseFinish}
              onChange={(e) => setCaseFinish(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Weight (g)</label>
            <input
              type='number'
              value={caseWeightG}
              onChange={(e) => setCaseWeightG(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
      </fieldset>

      {/* ── Dial & Hands ───────────────────────────────────────────── */}
      <fieldset className={fieldsetClass}>
        <legend className={legendClass}>Dial &amp; Hands</legend>
        <div className='grid grid-cols-2 gap-4 sm:grid-cols-3'>
          <div>
            <label className={labelClass}>Color</label>
            <input
              type='text'
              value={dialColor}
              onChange={(e) => setDialColor(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Material</label>
            <input
              type='text'
              value={dialMaterial}
              onChange={(e) => setDialMaterial(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Finish</label>
            <input
              type='text'
              value={dialFinish}
              onChange={(e) => setDialFinish(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Hands</label>
            <input
              type='text'
              value={dialHands}
              onChange={(e) => setDialHands(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Hands color</label>
            <input
              type='text'
              value={dialHandsColor}
              onChange={(e) => setDialHandsColor(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Marker Type</label>
            <input
              type='text'
              value={dialIndexes}
              onChange={(e) => setDialIndexes(e.target.value)}
              className={inputClass}
              placeholder='e.g. applied, printed'
            />
          </div>
          <div>
            <label className={labelClass}>Numerals</label>
            <input
              type='text'
              value={dialNumerals}
              onChange={(e) => setDialNumerals(e.target.value)}
              className={inputClass}
              placeholder='e.g. arabic, baton'
            />
          </div>
          <div>
            <label className={labelClass}>Subdials</label>
            <input
              type='number'
              value={dialSubdials}
              onChange={(e) => setDialSubdials(e.target.value)}
              className={inputClass}
            />
          </div>
          <label className='flex items-end gap-2 pb-2 text-sm text-gray-700'>
            <input
              type='checkbox'
              checked={dialLuminova}
              onChange={(e) => setDialLuminova(e.target.checked)}
              className='h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500'
            />
            Luminova
          </label>
        </div>
      </fieldset>

      {/* ── Bezel & Crystal ────────────────────────────────────────── */}
      <fieldset className={fieldsetClass}>
        <legend className={legendClass}>Bezel &amp; Crystal</legend>
        <div className='grid grid-cols-2 gap-4 sm:grid-cols-3'>
          <div>
            <label className={labelClass}>Bezel</label>
            <input
              type='text'
              value={bezel}
              onChange={(e) => setBezel(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Bezel type</label>
            <input
              type='text'
              value={bezelType}
              onChange={(e) => setBezelType(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Crown type</label>
            <input
              type='text'
              value={crownType}
              onChange={(e) => setCrownType(e.target.value)}
              className={inputClass}
              placeholder='e.g. screw_down, twinlock'
            />
          </div>
          <div>
            <label className={labelClass}>Glass</label>
            <input
              type='text'
              value={glass}
              onChange={(e) => setGlass(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Crystal</label>
            <input
              type='text'
              value={crystal}
              onChange={(e) => setCrystal(e.target.value)}
              className={inputClass}
              placeholder='e.g. sapphire_crystal, cyclops_lens'
            />
          </div>
          <div>
            <label className={labelClass}>Dial</label>
            <input
              type='text'
              value={dial}
              onChange={(e) => setDial(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
      </fieldset>

      {/* ── Movement ───────────────────────────────────────────────── */}
      {/* Conditionally shown based on the selected watchType's `showMovement`
          property (see `renderMovementSection`). Hidden state is preserved:
          the `movement` object stays in form state and is still written on
          save, so switching watchType back and forth is non-destructive. */}
      {renderMovementSection && (
      <fieldset className={fieldsetClass}>
        <legend className={legendClass}>Movement</legend>

        <CalibrePicker
          value={calibre}
          onChange={setCalibre}
          onRequestNewValue={({ query }) =>
            setRequestModal({
              type: 'calibre',
              sourceField: 'calibre',
              seed: query,
            })
          }
          requestSent={sentRequestFields.has('calibre')}
        />

        <div>
          <label className={labelClass}>Movement type</label>
          <input
            type='text'
            value={movementType}
            onChange={(e) => setMovementType(e.target.value)}
            className={inputClass}
          />
        </div>

        <div className='grid grid-cols-2 gap-4 sm:grid-cols-3'>
          <div>
            <label className={labelClass}>Frequency (vph)</label>
            <input
              type='number'
              value={frequencyVph}
              onChange={(e) => setFrequencyVph(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Jewels</label>
            <input
              type='number'
              value={jewels}
              onChange={(e) => setJewels(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Power reserve (h)</label>
            <input
              type='number'
              value={powerReserveHours}
              onChange={(e) => setPowerReserveHours(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Movement diameter (mm)</label>
            <input
              type='number'
              value={movementDiameterMm}
              onChange={(e) => setMovementDiameterMm(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Movement height (mm)</label>
            <input
              type='number'
              value={movementHeightMm}
              onChange={(e) => setMovementHeightMm(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label className={labelClass}>Base movement</label>
          <input
            type='text'
            value={baseMovement}
            onChange={(e) => setBaseMovement(e.target.value)}
            className={inputClass}
          />
        </div>

        <div className='grid grid-cols-2 gap-3 sm:grid-cols-4'>
          {(
            [
              ['Chronometer', isChronometer, setIsChronometer],
              ['COSC', isCosc, setIsCosc],
              ['Geneva Seal', isGenevaSeal, setIsGenevaSeal],
              ['METAS', isMETAS, setIsMETAS],
              ['Chronograph', isChronograph, setIsChronograph],
              ['GMT', isGMT, setIsGMT],
              ['In-house', isInHouse, setIsInHouse],
            ] as Array<[string, boolean, (v: boolean) => void]>
          ).map(([flagLabel, checked, setter]) => (
            <label
              key={flagLabel}
              className='flex items-center gap-2 text-sm text-gray-700'
            >
              <input
                type='checkbox'
                checked={checked}
                onChange={(e) => setter(e.target.checked)}
                className='h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500'
              />
              {flagLabel}
            </label>
          ))}
        </div>

        <TagInput
          label='Functions'
          values={functions}
          onChange={setFunctions}
          placeholder='Add a function…'
        />
      </fieldset>
      )}

      {/* ── Electronics (digital/hybrid) ───────────────────────────── */}
      {/* Conditionally shown based on the selected watchType's `showModule`
          property (see `renderElectronicsSection`). Like Movement, the
          `electronics` object persists in form state even while hidden. */}
      {renderElectronicsSection && (
      <fieldset className={fieldsetClass}>
        <legend className={legendClass}>Electronics (digital/hybrid)</legend>
        <div className='grid grid-cols-2 gap-4 sm:grid-cols-3'>
          <div>
            <label className={labelClass}>Module</label>
            <input
              type='text'
              value={elecModule}
              onChange={(e) => setElecModule(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Battery</label>
            <input
              type='text'
              value={elecBattery}
              onChange={(e) => setElecBattery(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Battery life</label>
            <input
              type='text'
              value={elecBatteryLife}
              onChange={(e) => setElecBatteryLife(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Charge time</label>
            <input
              type='text'
              value={elecChargeTime}
              onChange={(e) => setElecChargeTime(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Display</label>
            <input
              type='text'
              value={elecDisplay}
              onChange={(e) => setElecDisplay(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>LCD type</label>
            <input
              type='text'
              value={elecLcdType}
              onChange={(e) => setElecLcdType(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Light type</label>
            <input
              type='text'
              value={elecLightType}
              onChange={(e) => setElecLightType(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
      </fieldset>
      )}

      {/* ── Strap ──────────────────────────────────────────────────── */}
      <fieldset className={fieldsetClass}>
        <legend className={legendClass}>Strap</legend>
        <div className='grid grid-cols-2 gap-4 sm:grid-cols-4'>
          <div>
            <label className={labelClass}>Material</label>
            <input
              type='text'
              value={strapMaterial}
              onChange={(e) => setStrapMaterial(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Color</label>
            <input
              type='text'
              value={strapColor}
              onChange={(e) => setStrapColor(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Width (mm)</label>
            <input
              type='number'
              value={strapWidth}
              onChange={(e) => setStrapWidth(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Buckle</label>
            <input
              type='text'
              value={strapBuckle}
              onChange={(e) => setStrapBuckle(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
        <label className='flex items-center gap-2 text-sm text-gray-700'>
          <input
            type='checkbox'
            checked={strapMicroAdjustable}
            onChange={(e) => setStrapMicroAdjustable(e.target.checked)}
            className='h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500'
          />
          Micro-adjustable
        </label>
      </fieldset>

      {/* ── Production ──────────────────────────────────────────────── */}
      <fieldset className={fieldsetClass}>
        <legend className={legendClass}>Production</legend>
        <div className='grid grid-cols-2 gap-4 sm:grid-cols-3'>
          <div>
            <label className={labelClass}>Start year</label>
            <input
              type='number'
              value={prodStartYear}
              onChange={(e) => setProdStartYear(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>End year</label>
            <input
              type='number'
              value={prodEndYear}
              onChange={(e) => setProdEndYear(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Release year</label>
            <input
              type='number'
              value={prodReleaseYear}
              onChange={(e) => setProdReleaseYear(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
        <div className='flex flex-wrap items-end gap-6'>
          <label className='flex items-center gap-2 pb-2 text-sm text-gray-700'>
            <input
              type='checkbox'
              checked={prodIsLimited}
              onChange={(e) => setProdIsLimited(e.target.checked)}
              className='h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500'
            />
            Limited
          </label>
          <div>
            <label className={labelClass}>Limited size</label>
            <input
              type='number'
              value={prodLimitedSize}
              onChange={(e) => setProdLimitedSize(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
      </fieldset>

      {/* ── Links ──────────────────────────────────────────────────── */}
      <fieldset className={fieldsetClass}>
        <legend className={legendClass}>Links</legend>
        <div>
          <label className={labelClass}>Product URL</label>
          <input
            type='url'
            value={productUrl}
            onChange={(e) => setProductUrl(e.target.value)}
            placeholder='https://…'
            className={inputClass}
          />
        </div>

        <div className='space-y-2'>
          <div className='flex items-center justify-between'>
            <span className='text-sm font-medium text-gray-700'>
              Other links
            </span>
            <button
              type='button'
              onClick={addLink}
              className='text-sm text-brand-600 hover:text-brand-700'
            >
              + Add link
            </button>
          </div>
          {links.map((link, index) => (
            <div key={index} className='flex items-center gap-2'>
              <input
                type='url'
                value={link.url}
                onChange={(e) => updateLink(index, { url: e.target.value })}
                placeholder='URL'
                className={`${inputClass} flex-1`}
              />
              <input
                type='text'
                value={link.title ?? ''}
                onChange={(e) => updateLink(index, { title: e.target.value })}
                placeholder='Title'
                className={`${inputClass} flex-1`}
              />
              <button
                type='button'
                onClick={() => removeLink(index)}
                title='Remove link'
                className='px-2 py-2 text-gray-400 hover:text-red-600'
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </fieldset>

      {/* ── Description ─────────────────────────────────────────────── */}
      <fieldset className={fieldsetClass}>
        <legend className={legendClass}>Description</legend>

        {/* AI-generated description (read-only) */}
        <div>
          <label className={labelClass}>AI-generated description</label>
          <div className='w-full min-h-[6rem] whitespace-pre-line rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-500'>
            {currentReference?.aiDescription?.trim() || 'No AI description yet'}
          </div>
        </div>

        {/* Toggle: use the brand-authored description instead */}
        <div className='flex items-start gap-3'>
          <input
            id='useAuthoredReferenceDescription'
            type='checkbox'
            checked={useAuthoredDescription}
            onChange={(e) => setUseAuthoredDescription(e.target.checked)}
            className='mt-1 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500'
          />
          <label htmlFor='useAuthoredReferenceDescription' className='text-sm'>
            <span className='font-medium text-gray-700'>
              Use my own description instead
            </span>
            <span className='block text-xs text-gray-400'>
              When on, shoppers see your description below instead of the AI one.
            </span>
          </label>
        </div>

        {/* Brand-authored description (editable) */}
        <div className={useAuthoredDescription ? undefined : 'opacity-60'}>
          <label className={labelClass}>Your description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={5}
            className={inputClass}
          />
        </div>
      </fieldset>

      {error && (
        <div className='rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700'>
          {error}
        </div>
      )}

      <div className='flex items-center justify-end gap-3 pt-2 border-t border-gray-100'>
        <button
          type='button'
          onClick={onClose}
          disabled={saving}
          className='px-4 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50'
        >
          Cancel
        </button>
        <button
          type='submit'
          disabled={saving || refInvalid}
          className='px-4 py-2 text-sm bg-brand-600 text-white rounded hover:bg-brand-700 disabled:opacity-50'
        >
          {currentMode === 'create'
            ? saving
              ? 'Creating…'
              : 'Create reference'
            : saving
              ? 'Saving…'
              : 'Save changes'}
        </button>
      </div>
    </form>

    {requestModal && (
      <RequestNewValueModal
        visible
        brandId={brandId}
        type={requestModal.type}
        collection={requestModal.collection}
        initialValue={requestModal.seed ?? ''}
        context={{
          sourceField: requestModal.sourceField,
          sourceReferenceId: currentReference?.id,
        }}
        onClose={() => setRequestModal(null)}
        onSubmitted={() => {
          const field = requestModal.sourceField;
          setSentRequestFields((prev) => new Set(prev).add(field));
        }}
      />
    )}
    </>
  );
}
